import { Button, Card, Checkbox, Tag, Typography } from 'antd'
import { Option, Record as EffectRecord } from 'effect'
import { createContext, type ReactElement, useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  formatKpiPlanDecision,
  type KPI_PLAN_KINDS,
  type KPI_PLAN_OPS,
  type KpiPlan,
  type KpiPlanDecision,
  type KpiPlanItem,
} from '../../domain/kpiPlan'

// 会話の表示（AgentChatPane）が渡す、プランのカードに要るもの
export interface KpiPlanContextValue {
  // 会話の指示から読んだ、プランごとの判断（kpiPlan.ts）
  decisions: Readonly<Record<string, KpiPlanDecision>>
  // 判断を送れるプランの ID。なければ null
  actionablePlanId: string | null
  // 判断の文面を、次の指示として送る
  send: (text: string) => void
}

export const KpiPlanContext = createContext<KpiPlanContextValue>({
  decisions: {},
  actionablePlanId: null,
  send: () => undefined,
})

/**
 * プランのカード（PdM の要件 C-8）。項目ごとにチェックし（初めはすべて実行）、「この内容で実行」で、実行する項目と除外する項目を送る。
 * 判断の後は、送った指示から決めた結果を出す。外した項目を実行から外すのは runtime の対応の後で、それまではツールごとの承認のカードで止まる。
 * 呼ぶ側は、プランが替わったら作り直すよう、key にプランの ID を渡す
 */
export const KpiPlanCard = ({ plan }: { plan: KpiPlan }): ReactElement => {
  const { t } = useTranslation()
  const { decisions, actionablePlanId, send } = useContext(KpiPlanContext)
  const decision = Option.getOrUndefined(EffectRecord.get(decisions, plan.planId))
  const isActionable = decision === undefined && actionablePlanId === plan.planId
  // チェックを外した項目の ID。実行する項目は、今のプランの項目から決める（回答が伸びて項目が変わっても、新しい項目は実行にする）
  const [excludedIds, setExcludedIds] = useState<readonly string[]>([])
  const executedIds = plan.items.map(({ id }) => id).filter((id) => !excludedIds.includes(id))
  const opNames: Record<(typeof KPI_PLAN_OPS)[number], string> = {
    create: t(`作る`),
    update: t(`変える`),
    archive: t(`ゴミ箱へ`),
    restore: t(`ゴミ箱から戻す`),
    goal: t(`目標`),
    group: t(`グループ`),
  }
  const kindNames: Record<(typeof KPI_PLAN_KINDS)[number], string> = {
    kpi: t(`KPI`),
    dashboard: t(`ダッシュボード`),
    sheet: t(`シート`),
    custom_view: t(`カスタムビュー`),
    custom_object: t(`カスタムオブジェクト`),
    goal_config: t(`目標設定`),
    user_group: t(`ユーザーグループ`),
    dimension_group: t(`軸グループ`),
  }
  const isChecked = (item: KpiPlanItem): boolean =>
    decision === undefined ? executedIds.includes(item.id) : decision.executed.includes(item.id)
  const decide = (kind: KpiPlanDecision['decision']): void => {
    send(
      formatKpiPlanDecision({
        planId: plan.planId,
        decision: kind,
        executed: kind === 'execute' ? executedIds : [],
        excluded: kind === 'execute' ? plan.items.map(({ id }) => id).filter((id) => !executedIds.includes(id)) : [],
        revisions: [],
      }),
    )
  }

  return (
    <Card size="small" title={plan.title} aria-label={t(`変更のプラン`)} className="mt-2" data-e2e="agent-kpi-plan">
      <div className="flex flex-col gap-2">
        {plan.items.map((item) => (
          <Checkbox
            key={item.id}
            checked={isChecked(item)}
            disabled={!isActionable}
            onChange={(event) => {
              setExcludedIds((current) =>
                event.target.checked ? current.filter((id) => id !== item.id) : [...current, item.id],
              )
            }}
          >
            <span className="flex flex-col">
              <span className="flex flex-wrap items-center gap-1">
                <Tag className="m-0" bordered={false}>
                  {opNames[item.op]}
                </Tag>
                <Tag className="m-0" bordered={false}>
                  {kindNames[item.kind]}
                </Tag>
                <Typography.Text strong>{item.name}</Typography.Text>
              </span>
              {item.details.map((line) => (
                <Typography.Text key={line} type="secondary" className="text-xs">
                  {line}
                </Typography.Text>
              ))}
            </span>
          </Checkbox>
        ))}
        {decision !== undefined && (
          <Typography.Text type="secondary" className="text-xs">
            {decision.decision === 'execute'
              ? t(`承認済み（{{items}}）`, { items: decision.executed.join(', ') })
              : t(`実行しませんでした`)}
          </Typography.Text>
        )}
        {isActionable && (
          <div className="flex gap-2">
            <Button
              type="primary"
              size="small"
              disabled={executedIds.length === 0}
              onClick={() => {
                decide('execute')
              }}
            >
              {t(`この内容で実行`)}
            </Button>
            <Button
              size="small"
              onClick={() => {
                decide('cancel')
              }}
            >
              {t(`やめる`)}
            </Button>
          </div>
        )}
      </div>
    </Card>
  )
}
